/**
 * Feature flag initialization logic
 * @module lib/feature-flags/initializeFeatures
 */

import * as flagrApi from "./flagrApi";
import { flags } from "./constants";
import { InternalFlags } from "./store";

/**
 * Creates a value flag definition (returns raw variant value)
 * @param {string} name - Flag key name
 * @returns {Object} Flag definition with name and init function
 */
const valueFlag = (name) => ({ name, init: (value) => value });

/**
 * Creates a boolean flag definition (converts 'true'/'false' strings to boolean)
 * @param {string} name - Flag key name
 * @returns {Object} Flag definition with name and init function
 */
const booleanFlag = (name) => ({ name, init: (value) => value === "true" });

/**
 * Array of flag definitions to initialize
 * Add new flags here after defining them in constants.js
 *
 * @example
 * // For a boolean flag:
 * booleanFlag(flags.FLAG_FEAT_MY_FEATURE)
 *
 * // For a flag with string variants:
 * valueFlag(flags.FLAG_FEAT_BUTTON_COLOR)
 */
const FLAGS = [
  // Add flags here as they are created in FLAGR
  valueFlag(flags.FLAG_SALESFORCE_MIGRATION_STATUS),
];

/**
 * Initializes feature flags for the current user
 *
 * This function:
 * 1. Applies any internal overrides (e.g., from /ab route)
 * 2. Fetches active flags from FLAGR
 * 3. Evaluates any new flags for this user
 * 4. Prunes inactive flags
 *
 * @param {Object} user - Current user object
 * @param {string} [user.universalId] - User's universal ID for targeting
 * @param {string} [user.primaryEmail] - User's email for targeting
 * @returns {Promise<Object>} Object mapping flag keys to their initialized values
 */
const initializeFeatures = async (user) => {
  // Apply any internal overrides from /ab
  let flagValues = {
    ...InternalFlags.get(),
  };

  // Get the list of active flags for this application
  let activeFlagKeys = new Set();
  try {
    activeFlagKeys = await flagrApi.fetchActiveFlagKeys();

    // Perform evaluations for any flags the user has not been evaluated for
    const userFlagKeys = new Set(Object.keys(flagValues));
    const flagKeysToEvaluate = Array.from(activeFlagKeys).filter(
      (k) => !userFlagKeys.has(k),
    );

    if (flagKeysToEvaluate.length > 0) {
      const flagEvaluations = await flagrApi.evaluateFlags(
        flagKeysToEvaluate,
        user,
      );
      flagValues = {
        ...flagValues,
        ...flagEvaluations,
      };
    }
  } catch (e) {
    // Report error to Raygun if available
    window.rg4js &&
      window.rg4js("send", {
        error: e,
        tags: ["flagr"],
      });
  }

  // Prune any inactive flags
  flagValues = Object.keys(flagValues)
    .filter((f) => activeFlagKeys.has(f))
    .reduce((acc, val) => {
      acc[val] = flagValues[val];
      return acc;
    }, {});

  // Initialize and return flag values
  return FLAGS.reduce((acc, flag) => {
    if (flag.name in flagValues) {
      return {
        ...acc,
        [flag.name]: flag.init(flagValues[flag.name]),
      };
    } else {
      return acc;
    }
  }, {});
};

export default initializeFeatures;
